// ============================================================================
// PROCEEDING TYPE SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { proceedingTypeEndpoints, PROCEEDING_TYPE_BASE_PATH } from '../endpoints/proceedingType.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  ProceedingType,
  ProceedingTypeListItem,
  CreateProceedingTypeRequest,
  UpdateProceedingTypeRequest,
  ProceedingTypeSearchRequest,
} from '../types/proceedingType.types';

class ProceedingTypeService {
  async getAll(params?: ProceedingTypeSearchRequest): Promise<PaginatedResponse<ProceedingTypeListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(PROCEEDING_TYPE_BASE_PATH, params)
      : proceedingTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<ProceedingType>> {
    const response = await get<ApiResponse<ProceedingType>>(proceedingTypeEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<ProceedingType>> {
    const response = await get<ApiResponse<ProceedingType>>(proceedingTypeEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateProceedingTypeRequest): Promise<ApiResponse<ProceedingType>> {
    const response = await post<ApiResponse<ProceedingType>>(proceedingTypeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateProceedingTypeRequest): Promise<ApiResponse<ProceedingType>> {
    const response = await put<ApiResponse<ProceedingType>>(proceedingTypeEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(proceedingTypeEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${proceedingTypeEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : proceedingTypeEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const proceedingTypeService = new ProceedingTypeService();
export default proceedingTypeService;
